/**
 * Time Slider — scrub or play a layer's data over a date field.
 *
 * Pick a layer, pick a date field on it, press Run: the map filters to the
 * selected date — moved either by dragging the slider, picking a date
 * straight from the dropdown, or pressing Play (Auto — stops at the end
 * rather than looping). Two ways to read "the selected date": Single date
 * shows only that one day's features (the default — most survey/incident
 * data is read a day at a time); Cumulative shows everything dated on or
 * before it, for watching a total build up over time. The slider's stops
 * and the dropdown's options are the SAME list — the real distinct calendar
 * days the data actually has, not an evenly-spaced guess across the min/max
 * range, so every step (dragged, picked, or auto-played) lands somewhere
 * the map's filtered result genuinely changes.
 *
 * The filtering itself is NOT new. It reuses the exact channel the Filter
 * tool already has — `ctx.onLayerChange(layerId, { filter, filterClauses,
 * filterInvert })`, read by `MapViewOL.tsx` into CQL_FILTER/LAYERDEFS for
 * service-backed layers and by `layers/localFilter.ts` for local ones — so
 * every layer source works the same way here it already does everywhere
 * else in the app.
 */

import { useEffect, useRef, useState } from 'react';
import { Loader2, Pause, Play } from 'lucide-react';
import ToolPanelHeader, { ToolActions } from '../ToolPanel';
import { scopedLayers, type ToolContext } from './types';
import { EmptyHint, Field, LayerSelect, Notice, btnClass, inputClass } from './shared';
import { fetchDateFieldNames, fetchDateValues } from '../../../tools/toolFields';
import { buildFilterExpression, type AttributeClause } from '../../layers/attributeQuery';

const SPEEDS: { id: 'slow' | 'medium' | 'fast'; label: string; intervalMs: number }[] = [
  { id: 'slow', label: 'Slow', intervalMs: 1600 },
  { id: 'medium', label: 'Medium', intervalMs: 800 },
  { id: 'fast', label: 'Fast', intervalMs: 300 },
];

export function TimeSliderPanel({ ctx }: { ctx: ToolContext }) {
  const allowed = scopedLayers(ctx.config, ctx.layers);

  const [layerId, setLayerId] = useState(allowed[0]?.id ?? '');
  const [dateField, setDateField] = useState('');
  const [dateFields, setDateFields] = useState<string[]>([]);
  const [fieldsBusy, setFieldsBusy] = useState(false);

  const [values, setValues] = useState<{ days: { day: string; ms: number }[] } | null>(null);
  /** Single date (default): only that day's features. Cumulative: everything
   *  on or before it. */
  const [mode, setMode] = useState<'single' | 'cumulative'>('single');
  const [stopIndex, setStopIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<'slow' | 'medium' | 'fast'>('medium');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const source = allowed.find((layer) => layer.id === layerId);
  const lastStop = values ? values.days.length - 1 : 0;
  const current = values ? values.days[Math.min(stopIndex, lastStop)] : null;

  /* Kept fresh every render so the unmount cleanup below always clears the
     filter through the LATEST onLayerChange, not whichever one happened to
     be in scope when the panel first mounted. */
  const onLayerChangeRef = useRef(ctx.onLayerChange);
  useEffect(() => {
    onLayerChangeRef.current = ctx.onLayerChange;
  });

  /** Which layer THIS panel's filter is currently sitting on, if any. */
  const appliedLayerIdRef = useRef<string | null>(null);
  const clearAppliedFilter = () => {
    const appliedId = appliedLayerIdRef.current;
    if (appliedId) {
      onLayerChangeRef.current?.(appliedId, {
        filter: undefined,
        filterClauses: undefined,
        filterInvert: undefined,
      });
      appliedLayerIdRef.current = null;
    }
  };
  // Puts the map back to unfiltered when this tool's tab closes — the same
  // teardown the Filter tool does when it closes.
  useEffect(() => {
    return () => clearAppliedFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Date fields for the chosen layer. Switching layers (or arriving on one)
  // always starts over — any dates/position from a previous layer no longer
  // mean anything.
  useEffect(() => {
    setDateField('');
    setDateFields([]);
    setValues(null);
    setStopIndex(0);
    setPlaying(false);
    if (!source) return;
    let live = true;
    setFieldsBusy(true);
    void fetchDateFieldNames(source, ctx.module).then((names) => {
      if (!live) return;
      setDateFields(Array.from(names).sort());
      setFieldsBusy(false);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerId]);

  const run = async () => {
    if (!source || !dateField) return;
    // Points the slider at a different layer/field than whatever was last
    // applied — clear that one first so it never gets left mid-filter.
    clearAppliedFilter();
    setError(null);
    setBusy(true);
    try {
      const found = await fetchDateValues(source, ctx.module, dateField);
      if (!found) {
        setValues(null);
        setError('No date values were found for that field on this layer.');
        return;
      }
      setValues(found);
      setStopIndex(0);
      setPlaying(false);
    } finally {
      setBusy(false);
    }
  };

  // Applies the filter whenever the slider's position (or the mode) changes.
  useEffect(() => {
    if (!source || !current) return;
    const dialect: 'cql' | 'sql' = source.arcgisLayerId === undefined ? 'cql' : 'sql';
    // Local layers go through matchesClauses's compare(), which now parses
    // BOTH sides as dates (see attributeQuery.ts's parseLooseDate) whatever
    // format the raw value is actually written in — "1/8/2026",
    // "2024-05-14", an epoch number, all work the same way. Service-backed
    // layers compare a date-only literal against the column's real date
    // type server-side, so no end-of-day suffix is needed there.
    const upperBound = source.localData ? `${current.day}T23:59:59` : current.day;

    const clauses: AttributeClause[] =
      mode === 'cumulative'
        ? [{ id: 'time-slider-upper', field: dateField, operator: 'lessOrEqual', value: upperBound, conjunction: 'and' }]
        : [
            // A raw value with a time-of-day ("2024-05-14T08:00:00Z") must
            // still count as THIS day, which a single `equals` clause would
            // miss — a >= start-of-day / <= end-of-day window catches it
            // regardless of the time portion.
            { id: 'time-slider-lower', field: dateField, operator: 'greaterOrEqual', value: current.day, conjunction: 'and' },
            { id: 'time-slider-upper', field: dateField, operator: 'lessOrEqual', value: upperBound, conjunction: 'and' },
          ];
    const expression = buildFilterExpression(clauses, dialect, false, new Set(), new Set([dateField]));
    onLayerChangeRef.current?.(source.id, {
      filter: expression,
      filterClauses: clauses,
      filterInvert: false,
    });
    appliedLayerIdRef.current = source.id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source?.id, dateField, stopIndex, values, mode]);

  // Auto-play: advances one stop per tick, stops (does not loop) at the end.
  useEffect(() => {
    if (!playing || !values || lastStop === 0) return;
    const intervalMs = SPEEDS.find((entry) => entry.id === speed)?.intervalMs ?? 800;
    const id = window.setInterval(() => {
      setStopIndex((prev) => {
        if (prev >= lastStop) {
          setPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [playing, speed, values, lastStop]);

  return (
    <>
      <ToolPanelHeader
        title="Time Slider"
        description="Scrub or play a layer's data over a date field."
        onClose={ctx.onClose}
      />

      <Field label="Layer">
        <LayerSelect layers={allowed} value={layerId} onChange={setLayerId} />
      </Field>

      <Field label="Date field">
        <select
          value={dateField}
          onChange={(event) => {
            setDateField(event.target.value);
            setValues(null);
            setStopIndex(0);
            setPlaying(false);
          }}
          disabled={!source || fieldsBusy || dateFields.length === 0}
          className={inputClass}
        >
          <option value="">{fieldsBusy ? 'Looking for date fields…' : 'Choose a date field…'}</option>
          {dateFields.map((field) => (
            <option key={field} value={field}>
              {field}
            </option>
          ))}
        </select>
      </Field>

      {source && !fieldsBusy && dateFields.length === 0 && (
        <EmptyHint>No date field was found on this layer.</EmptyHint>
      )}

      <ToolActions>
        <button
          type="button"
          onClick={() => void run()}
          disabled={!source || !dateField || busy}
          className={`${btnClass} gl-btn--primary`}
        >
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {busy ? 'Loading…' : 'Run'}
        </button>
      </ToolActions>

      {error && <Notice tone="warn">{error}</Notice>}

      {values && current && (
        <div className="space-y-2">
          {/* Jumps straight to any one of the dates the data actually has —
              the direct alternative to dragging the slider. */}
          <Field label="Date">
            <select
              value={stopIndex}
              onChange={(event) => {
                setPlaying(false);
                setStopIndex(Number(event.target.value));
              }}
              className={inputClass}
            >
              {values.days.map((entry, index) => (
                <option key={entry.day} value={index}>
                  {entry.day}
                </option>
              ))}
            </select>
          </Field>

          <div className="flex gap-1 rounded-md bg-slate-100 p-0.5 text-[11px] font-medium">
            <button
              type="button"
              onClick={() => setMode('single')}
              className={`flex-1 rounded px-2 py-1 transition ${
                mode === 'single' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Single date
            </button>
            <button
              type="button"
              onClick={() => setMode('cumulative')}
              className={`flex-1 rounded px-2 py-1 transition ${
                mode === 'cumulative' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Cumulative
            </button>
          </div>

          <input
            type="range"
            min={0}
            max={lastStop}
            step={1}
            value={stopIndex}
            disabled={lastStop === 0}
            onChange={(event) => {
              setPlaying(false);
              setStopIndex(Number(event.target.value));
            }}
            className="w-full"
          />
          <div className="flex items-center justify-between text-[10.5px] text-slate-400">
            <span>{values.days[0].day}</span>
            <span>{values.days[lastStop].day}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPlaying((prev) => !prev)}
              disabled={lastStop === 0}
              className={`${btnClass} gl-btn--secondary`}
            >
              {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
              {playing ? 'Pause' : 'Play'}
            </button>

            <div className="flex flex-1 gap-1 rounded-md bg-slate-100 p-0.5 text-[11px] font-medium">
              {SPEEDS.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => setSpeed(entry.id)}
                  className={`flex-1 rounded px-2 py-1 transition ${
                    speed === entry.id
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {entry.label}
                </button>
              ))}
            </div>
          </div>

          <p className="text-[10.5px] leading-snug text-slate-400">
            {mode === 'single'
              ? `The map shows only features dated ${current.day}`
              : `The map shows every feature dated on or before ${current.day}`}{' '}
            ({values.days.length} date{values.days.length === 1 ? '' : 's'} total). Play stops at the
            end instead of looping.
          </p>
        </div>
      )}
    </>
  );
}
